/**
 * H5 公共属性真相源（与 Mobile appInfo 对齐，字段走 api.md）。
 */

import { userState } from '../stores/user'
import { getAnalyticsChannel } from './config'

const DEVICE_ID_KEY = 'DIFI_DEVICE_ID'
const SESSION_ID_KEY = 'DIFI_ANALYTICS_SESSION_ID'

export type AppInfoSnapshot = {
  device_id: string
  os: string
  device_type: string
  app_version: string
  scre: string
  la: string
  bw: 'wifi' | 'cellular' | 'none' | ''
  channel: string
  session_id: string
  user_id: string | null
  ua: string
  country: string
  ips: string
}

let sessionId: string | null = null
let cachedDeviceId: string | null = null

function randomId(prefix: string): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fall through */
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

export function isInAppWebView(): boolean {
  return typeof window !== 'undefined' && !!(window as any).ReactNativeWebView
}

function resolveDeviceId(): string {
  if (cachedDeviceId) return cachedDeviceId
  const fromDeviceInfo = resolveDeviceIdFromApp()
  if (fromDeviceInfo) {
    cachedDeviceId = fromDeviceInfo
    return cachedDeviceId
  }
  try {
    const fromLs = localStorage.getItem(DEVICE_ID_KEY)?.trim()
    if (fromLs) {
      cachedDeviceId = fromLs
      return cachedDeviceId
    }
    const id = randomId('did')
    localStorage.setItem(DEVICE_ID_KEY, id)
    cachedDeviceId = id
    return id
  } catch {
    cachedDeviceId = randomId('did')
    return cachedDeviceId
  }
}

function resolveSessionId(): string {
  if (sessionId) return sessionId
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY)?.trim()
    if (existing) {
      sessionId = existing
      return sessionId
    }
    sessionId = randomId('sess')
    sessionStorage.setItem(SESSION_ID_KEY, sessionId)
    return sessionId
  } catch {
    sessionId = randomId('sess')
    return sessionId
  }
}

function resolveOs(): string {
  const fromApp = userState.deviceInfo as { osName?: string; osVersion?: string } | null
  if (fromApp?.osName) {
    const ver = fromApp.osVersion ? ` ${fromApp.osVersion}` : ''
    return `${String(fromApp.osName).toLowerCase()}${ver}`.trim()
  }
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  if (/iPhone|iPad|iOS/i.test(ua)) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return 'web'
}

function resolveDeviceType(): string {
  const fromApp = userState.deviceInfo as { deviceName?: string } | null
  return fromApp?.deviceName?.trim() || ''
}

function resolveAppVersion(): string {
  const fromApp = userState.deviceInfo as { appVersion?: string } | null
  return fromApp?.appVersion?.trim() || 'h5'
}

function resolveDeviceIdFromApp(): string {
  const fromApp = userState.deviceInfo as { deviceId?: string } | null
  return fromApp?.deviceId?.trim() || ''
}

function resolveScre(): string {
  if (typeof window === 'undefined') return ''
  const dpr = window.devicePixelRatio || 1
  return `${Math.round(window.innerWidth)}x${Math.round(window.innerHeight)}@${dpr.toFixed(2)}`
}

function resolveLa(): string {
  try {
    return localStorage.getItem('lang') || navigator.language || 'zh-CN'
  } catch {
    return navigator.language || 'zh-CN'
  }
}

function countryFromLocale(la: string): string {
  const m = /^[A-Za-z]{2,3}[-_]([A-Za-z]{2})\b/.exec(la.trim())
  return m ? m[1].toUpperCase() : ''
}

function resolveBw(): AppInfoSnapshot['bw'] {
  if (typeof navigator === 'undefined') return ''
  if (navigator.onLine === false) return 'none'
  const conn = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection
  const type = String(conn?.type || conn?.effectiveType || '').toLowerCase()
  if (type === 'wifi') return 'wifi'
  if (type === 'cellular' || type === 'cell' || /^(2g|3g|4g|5g)/.test(type)) return 'cellular'
  return navigator.onLine ? 'wifi' : 'none'
}

function resolveUserId(): string | null {
  const id = userState.id
  if (id == null || id === '') return null
  const text = String(id).trim()
  return text || null
}

export function getAppInfo(): AppInfoSnapshot {
  const os = resolveOs()
  const device_type = resolveDeviceType()
  const app_version = resolveAppVersion()
  const la = resolveLa()
  return {
    device_id: resolveDeviceId(),
    os,
    device_type,
    app_version,
    scre: resolveScre(),
    la,
    bw: resolveBw(),
    channel: getAnalyticsChannel(),
    session_id: resolveSessionId(),
    user_id: resolveUserId(),
    ua: typeof navigator !== 'undefined' ? navigator.userAgent : `DiFiH5/${app_version}`,
    country: countryFromLocale(la),
    ips: ''
  }
}

export function ensureAppInfoReady(): Promise<void> {
  resolveDeviceId()
  resolveSessionId()
  return Promise.resolve()
}
